/*
 * This file is part of the Web3 Library developed by mFactory GmbH.
 *
 * Copyright © 2021, mFactory GmbH
 *
 * Solana Reference Stake Pool is free software: you can redistribute it
 * and/or modify it under the terms of the GNU Affero General Public License
 * as published by the Free Software Foundation, either version 3
 * of the License, or (at your option) any later version.
 *
 * Solana Reference Stake Pool is distributed in the hope that it
 * will be useful, but WITHOUT ANY WARRANTY; without even the implied
 * warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.
 * If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
 *
 * You can be released from the requirements of the Affero GNU General Public License
 * by purchasing a commercial license. The purchase of such a license is
 * mandatory as soon as you develop commercial activities using the
 * Solana Reference Stake Pool code without disclosing the source code of
 * your own applications.
 *
 * The developer of this program can be contacted at <info@mfactory.ch>.
 */

import type {
  Signer,
  TransactionInstruction,
} from '@solana/web3.js'
import type { ProgramAccount } from '~/store'
import { depositStake } from '@solana/spl-stake-pool'
import { useAnchorWallet, useWallet } from '@solana/wallet-adapter-vue'
import {
  Authorized,
  LAMPORTS_PER_SOL,
  PublicKey,
  StakeProgram,
} from '@solana/web3.js'
import { useQuasar } from 'quasar'
import { computed, ref } from 'vue'
import { withPriorityFees } from '~/features/priority-fee'
import { useMonitorTransaction } from '~/hooks'
import {
  useConnectionStore,
  useStakeAccountStore,
  useStakePoolStore,
  useValidatorActiveStore,
} from '~/store'
import { sendTransaction } from '~/utils'

function dec2hex(dec: number) {
  return dec.toString(16).padStart(2, '0')
}

function generateId(len?: number) {
  const arr = new Uint8Array((len || 32) / 2)
  globalThis.crypto.getRandomValues(arr)
  return Array.from(arr, dec2hex).join('')
}

export async function findFirstAvailableSeed(publicKey: PublicKey,
  stakeAccounts: ProgramAccount[]): Promise<string> {
  let seed = generateId()
  if (!publicKey) {
    return ''
  }
  while (1) {
    const newStakeAccountPubkey = await PublicKey.createWithSeed(
      publicKey,
      seed,
      StakeProgram.programId,
    )
    const matching = stakeAccounts.find(meta => newStakeAccountPubkey.equals(meta.pubkey))
    if (!matching) {
      break
    }
    seed = generateId()
  }
  return seed
}

export async function createStakePubkeyWithSeed(publicKey: PublicKey,
  stakeAccounts: ProgramAccount[]) {
  const seed = await findFirstAvailableSeed(publicKey, stakeAccounts)
  const stakePubkey = await PublicKey.createWithSeed(publicKey, seed, StakeProgram.programId)
  return {
    seed,
    stakePubkey,
  }
}

export function useStakeAccounts() {
  const connectionStore = useConnectionStore()
  const stakeAccountStore = useStakeAccountStore()
  const validatorActiveStore = useValidatorActiveStore()
  const stakePoolStore = useStakePoolStore()
  const { publicKey } = useWallet()
  const anchorWallet = useAnchorWallet()
  const { monitorTransaction, sending } = useMonitorTransaction()
  const { notify } = useQuasar()

  const loading = ref(false)
  const seed = ref('0')

  const lamportsPerSignature = computed(() => stakePoolStore.lamportsPerSignature)
  const voterKey = computed(() => validatorActiveStore.voterKey)

  // const balanceStore = useBalanceStore()
  // const nativeBalance = computed(() => balanceStore.nativeBalance)

  const delegateAccount = async (stakePubkeys: PublicKey[], reloadStakes = false) => {
    if (!publicKey.value) {
      return
    }
    try {
      const transactions: TransactionInstruction[] = []

      for (const stakePubkey of stakePubkeys) {
        if (publicKey.value) {
          const instructions = StakeProgram.delegate({
            stakePubkey,
            authorizedPubkey: publicKey.value,
            votePubkey: new PublicKey(voterKey.value),
          }).instructions

          transactions.push(...await withPriorityFees({ instructions }))
        }
      }

      await monitorTransaction(
        sendTransaction(connectionStore.connection, anchorWallet.value!, transactions, []),
        {
          commitment: 'finalized',
          onSuccess: async () => {
            if (reloadStakes) {
              await stakeAccountStore.load({
                // delay: 3000,
              })
            }
          },
          onError: () => {},
        },
      )
    } catch (error: any) {
      notify({ message: error.message, type: 'negative' })
      throw error
    }
  }

  return {
    depositFee: computed(() => lamportsPerSignature.value),
    creating: computed(() => loading.value || sending.value),
    createAccount: async (amount: number) => {
      if (!publicKey.value) {
        return
      }
      try {
        loading.value = true

        const stakeData = await createStakePubkeyWithSeed(publicKey.value, stakeAccountStore.data)
        seed.value = stakeData.seed
        const stakePubkey = stakeData.stakePubkey

        const transaction = StakeProgram.createAccountWithSeed({
          fromPubkey: publicKey.value,
          stakePubkey,
          basePubkey: publicKey.value,
          seed: seed.value,
          authorized: new Authorized(publicKey.value, publicKey.value),
          lamports: LAMPORTS_PER_SOL * amount,
        })

        const transactionDelegate = StakeProgram.delegate({
          stakePubkey,
          authorizedPubkey: publicKey.value,
          votePubkey: new PublicKey(voterKey.value),
        })

        const instructions = [...transaction.instructions, ...transactionDelegate.instructions]

        await monitorTransaction(
          sendTransaction(
            connectionStore.connection,
            anchorWallet.value!,
            await withPriorityFees({ instructions }),
            [],
          ),
          {
            commitment: 'finalized',
            onSuccess: async () => {
              await stakeAccountStore.load({
                // delay: 3000,
              })
            },
            onError: () => {},
          },
        )
      } catch (error: any) {
        notify({ message: error.message, type: 'negative' })
        throw error
      } finally {
        loading.value = false
      }
    },
    delegateAccount,
    /**
     * @param stakeAccounts
     */
    depositStake: async (stakeAccounts: ProgramAccount[]) => {
      try {
        if (!voterKey.value) {
          // noinspection ExceptionCaughtLocallyJS
          throw new Error('Wrong stake account state, must be delegated to validator')
        }

        const transactions: TransactionInstruction[] = []
        // let rentFeeTotal = 0
        let signersTransaction: Signer[] = []
        for (const stakeAccount of stakeAccounts) {
          if (publicKey.value) {
            const { instructions, signers } = await depositStake(
              connectionStore.connection,
              connectionStore.stakePoolAddress!,
              anchorWallet.value!.publicKey!,
              new PublicKey(voterKey.value),
              stakeAccount.pubkey,
            )

            // TODO: fix this
            // const rentFee = await connectionStore.connection.getMinimumBalanceForRentExemption(AccountLayout.span)

            signersTransaction = signers

            // rentFeeTotal += rentFee
            transactions.push(...await withPriorityFees({ instructions }))
          }
        }

        // if (nativeBalance.value < rentFeeTotal) {
        //   // noinspection ExceptionCaughtLocallyJS
        //   throw new Error(
        //     `Insufficient balance, at least ${lamportsToSol(rentFeeTotal)} SOL are required.`,
        //   )
        // }

        let success = false

        await monitorTransaction(
          sendTransaction(
            connectionStore.connection,
            anchorWallet.value!,
            transactions,
            signersTransaction,
          ),
          {
            onSuccess: () => (success = true),
          },
        )

        return success
      } catch (error: any) {
        notify({ message: error.message, type: 'negative' })
        throw error
      } finally {
        loading.value = false
      }
    },
  }
}
